import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import ModalInDrawer from "../ui/ModalInDrawer";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const ConfirmsModal = ({
  activeModal,
  onClose,
  confirmacionesServicio,
  setConfirmacionesServicio,
  data,
  pagos,
  rowValues,
  onSave,
  processedConfirmaciones = null,
  readOnly = false,
}) => {
  const [localContent, setLocalContent] = useState("");
  const quillRef = useRef(null);

  // Función para normalizar el contenido a formato Delta
  const normalizeToDelta = useCallback((content) => {
    if (!content) {
      return { ops: [{ insert: '\n' }] };
    }

    // Si ya es un objeto Delta con ops, retornarlo directamente
    if (typeof content === "object" && content.ops && Array.isArray(content.ops)) {
      return content;
    }

    // Si es un string (HTML o texto plano), convertirlo a Delta usando Quill
    if (typeof content === "string") {
      try {
        const Delta = Quill.import('delta');
        const tempQuill = new Quill(document.createElement('div'));
        // Usar dangerouslyPasteHTML para convertir HTML a Delta
        tempQuill.clipboard.dangerouslyPasteHTML(content);
        const delta = tempQuill.getContents();
        // Verificar que el delta tenga contenido válido
        if (delta && delta.ops && delta.ops.length > 0) {
          return delta;
        }
        // Si no tiene contenido, crear un Delta básico con el texto
        return new Delta().insert(content + '\n');
      } catch (e) {
        console.warn("Error convirtiendo HTML a Delta:", e);
        const Delta = Quill.import('delta');
        return new Delta().insert(content + '\n');
      }
    }

    // Si es un array, tomar el primer elemento y normalizarlo
    if (Array.isArray(content) && content.length > 0) {
      return normalizeToDelta(content[0]);
    }

    // Si es otro tipo de objeto, intentar convertirlo a string y luego a Delta
    try {
      const Delta = Quill.import('delta');
      const tempQuill = new Quill(document.createElement('div'));
      tempQuill.clipboard.dangerouslyPasteHTML(String(content));
      return tempQuill.getContents();
    } catch (e) {
      console.warn("Error normalizando objeto a Delta:", e);
      const Delta = Quill.import('delta');
      return new Delta().insert(JSON.stringify(content) + '\n');
    }
  }, []);

  // Función para procesar templates y reemplazar placeholders
  const processTemplate = useCallback((template) => {
    if (!template) {
      return "";
    }
    // Si es un array, tomar el primer elemento
    if (Array.isArray(template)) {
      template = template.length > 0 ? template[0] : null;
      if (!template) return "";
    }

    // Preparar datos dinámicos para reemplazo
    const now = new Date();
    const cotizacion = data?.cotizacion || {};
    const cliente = cotizacion?.cliente || {};
    const lead = cotizacion?.lead || {};
    const cotizador = cotizacion?.cotizador || {};

    const nombreServicio = data?.servicio?.name || "N/A";
    const numeroCotizacion = cotizacion?.numeroCotizacion || "N/A";
    const nombreVendedor = cotizador?.name || "N/A";
    const nombreCliente = cliente?.nombreGerente || lead?.leadName || "N/A";
    const dniCliente = cliente?.dni || lead?.leadDNI || "N/A";
    const empresaCliente = cliente?.nombreComercial || lead?.leadCompany || data?.cotizacion?.empresa?.razonSocial || "Monstruo Creativo S.A.C";

    const fecha = format(now, "dd 'de' MMMM 'de' yyyy", { locale: es });
    const hora = format(now, "HH:mm", { locale: es });
    
    // Calcular monto total desde los pagos recibidos como prop
    const montoTotal = (pagos || []).reduce((acc, p) => acc + (p.precio || 0) + ((p.montosServicios?.reduce((acc, ms) => acc + (ms.monto || 0), 0)) || 0), 0).toLocaleString("es-PE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const incluyeIGV = rowValues?.igv === "si" ? "Incluye IGV" : "No incluye IGV";
    const cantidadCuotas = (pagos || []).length > 0 ? pagos.length : 1;

    // Función helper para reemplazar placeholders en un texto
    const replacePlaceholders = (text) => {
      if (!text || typeof text !== "string") return text;
      return text
        .replace(/{nombreServicio}/g, nombreServicio)
        .replace(/{numeroCotizacion}/g, numeroCotizacion)
        .replace(/{nombreVendedor}/g, nombreVendedor)
        .replace(/{nombreCliente}/g, nombreCliente)
        .replace(/{dniCliente}/g, dniCliente)
        .replace(/{empresaCliente}/g, empresaCliente)
        .replace(/{fecha}/g, fecha)
        .replace(/{hora}/g, hora)
        .replace(/{montoTotal}/g, `S/. ${montoTotal}`)
        .replace(/{incluyeIGV}/g, incluyeIGV)
        .replace(/{cantidadCuotas}/g, cantidadCuotas.toString());
    };

    // Caso 1: Es un objeto Delta de Quill (tiene propiedad ops)
    if (template && typeof template === "object" && template.ops && Array.isArray(template.ops) && template.ops.length >= 0) {
      // Clonar el Delta y reemplazar placeholders en los inserts manteniendo el formato
      const processedDelta = {
        ...template,
        ops: template.ops.map((op) => {
          // Si el insert es un string, reemplazar placeholders
          if (typeof op.insert === "string") {
            return {
              ...op,
              insert: replacePlaceholders(op.insert),
            };
          }
          // Si el insert es un objeto (por ejemplo, imágenes, embeds), mantenerlo tal cual
          return op;
        }),
      };
      return processedDelta;
    }

    // Caso 2: Es un string HTML
    if (typeof template === "string") {
      return replacePlaceholders(template);
    }

    // Caso 3: Es un objeto con propiedad html o content
    if (template && typeof template === "object") {
      if (template.html && typeof template.html === "string") {
        return replacePlaceholders(template.html);
      }
      if (template.content && typeof template.content === "string") {
        return replacePlaceholders(template.content);
      }
      // Si es un objeto pero no tiene estructura conocida, intentar stringify y reemplazar
      try {
        const jsonString = JSON.stringify(template);
        const processedJsonString = replacePlaceholders(jsonString);
        // Intentar parsear de vuelta a objeto
        return JSON.parse(processedJsonString);
      } catch (e) {
        console.warn("No se pudo procesar el objeto template:", template);
        return "";
      }
    }

    // Caso 4: Cualquier otro tipo, convertir a string y reemplazar
    return replacePlaceholders(String(template || ""));
  }, [data, pagos, rowValues?.igv]);

  // Función helper para detectar si el contenido tiene placeholders (es un template)
  const hasPlaceholders = useCallback((content) => {
    if (!content) return false;
    const contentStr = typeof content === "string" 
      ? content 
      : (content.ops && Array.isArray(content.ops))
        ? content.ops.map(op => typeof op.insert === "string" ? op.insert : "").join("")
        : JSON.stringify(content);
    
    const placeholderPattern = /\{(nombreServicio|numeroCotizacion|nombreVendedor|nombreCliente|dniCliente|empresaCliente|fecha|hora|montoTotal|incluyeIGV|cantidadCuotas)\}/g;
    return placeholderPattern.test(contentStr);
  }, []);

  // Inicializar el contenido cuando se abre el modal o cambian las confirmaciones
  useEffect(() => {
    if (activeModal) {
      // Si hay confirmaciones procesadas y estamos en modo lectura, usarlas directamente
      if (readOnly && processedConfirmaciones !== null && processedConfirmaciones !== undefined) {
        setLocalContent(processedConfirmaciones);
        return;
      }

      // En modo edición
      const rawConfirmaciones = confirmacionesServicio;
      let initialContent = "";

      // Si es un array, tomar el primer elemento
      if (Array.isArray(rawConfirmaciones) && rawConfirmaciones.length > 0) {
        initialContent = rawConfirmaciones[0];
      } else if (rawConfirmaciones && typeof rawConfirmaciones === "object") {
        initialContent = rawConfirmaciones;
      } else if (rawConfirmaciones) {
        initialContent = rawConfirmaciones;
      }

      // Si el contenido ya está editado (no tiene placeholders), usarlo directamente
      // Si tiene placeholders, procesarlo como template
      let contentToSet = "";
      if (initialContent && !hasPlaceholders(initialContent)) {
        // Contenido ya editado, usar directamente pero normalizar a Delta
        contentToSet = normalizeToDelta(initialContent);
      } else {
        // Es un template, procesarlo para reemplazar placeholders
        const processed = processTemplate(initialContent);
        contentToSet = normalizeToDelta(processed || "");
      }
      setLocalContent(contentToSet);
    }
  }, [activeModal, confirmacionesServicio, processedConfirmaciones, processTemplate, readOnly, hasPlaceholders, normalizeToDelta]);

  // Manejar cambios en el editor
  const handleChange = (value, delta, source, editor) => {
    // React Quill devuelve el contenido como Delta en el parámetro value
    // Asegurarnos de que siempre sea un objeto Delta
    if (value && typeof value === "object" && value.ops) {
      setLocalContent(value);
    } else {
      // Si por alguna razón no es Delta, obtenerlo del editor
      const editorContent = editor?.getContents();
      setLocalContent(editorContent || { ops: [{ insert: '\n' }] });
    }
  };

  // Guardar los cambios solo en el estado local (no en el backend)
  const handleSave = () => {
    // Normalizar el contenido a formato Delta antes de guardar
    const normalizedContent = normalizeToDelta(localContent);
    
    // Actualizar el estado local del componente padre
    if (setConfirmacionesServicio) {
      setConfirmacionesServicio(normalizedContent);
    }
    if (onSave) {
      onSave(normalizedContent);
    }
    // Cerrar el modal - el guardado real se hará desde el botón "Guardar" del servicio
    onClose();
  };

  // Obtener el valor para ReactQuill
  const getQuillValue = () => {
    // En modo lectura, si hay confirmaciones procesadas, usarlas
    if (readOnly && processedConfirmaciones !== null && processedConfirmaciones !== undefined) {
      return processedConfirmaciones;
    }
    
    // Usar el contenido local (que puede estar editado)
    if (localContent) {
      return localContent;
    }
    
    // Fallback: usar confirmaciones sin procesar
    const rawConfirmaciones = confirmacionesServicio;
    if (Array.isArray(rawConfirmaciones) && rawConfirmaciones.length > 0) {
      return rawConfirmaciones[0];
    }
    if (rawConfirmaciones && typeof rawConfirmaciones === "object") {
      return rawConfirmaciones;
    }
    return "";
  };

  return (
    <ModalInDrawer
      activeModal={activeModal}
      onClose={onClose}
      title="Confirmaciones del servicio"
      className="max-w-5xl"
      centered
    >
      <div className="flex flex-col text-xs">
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={getQuillValue()}
          onChange={readOnly ? undefined : handleChange}
          readOnly={readOnly}
          modules={{
            toolbar: readOnly ? false : [
              [{ 'header': [1, 2, 3, false] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ 'list': 'ordered'}, { 'list': 'bullet' }],
              [{ 'color': [] }, { 'background': [] }],
              ['link'],
              ['clean']
            ],
          }}
          formats={[
            'header',
            'bold', 'italic', 'underline', 'strike',
            'list', 'bullet',
            'color', 'background',
            'link'
          ]}
        />
        {!readOnly && (
          <div className="flex flex-col gap-2 mt-4">
            <div className="text-xs text-gray-500 italic text-center">
              Los cambios se aplicarán al guardar el servicio desde la fila
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Aplicar cambios
              </button>
            </div>
          </div>
        )}
      </div>
    </ModalInDrawer>
  );
};

export default ConfirmsModal;
